import {
  AutocompleteRenderGetTagProps,
  AutocompleteRenderOptionState,
  AutocompleteRenderInputParams,
  AutocompleteChangeReason,
  AutocompleteChangeDetails,
  ClickAwayListener,
  MenuItem,
  SvgIcon,
  Typography,
} from '@mui/material';
import { FunctionComponent, ReactNode, useState, useEffect } from 'react';

import Checkbox from '@Components/Checkbox';
import Loader from '@Components/Loader';
import TextField from '@Components/TextField';
import Icons from '@Constants/Icons.constants';
import { CommonConstants } from '@Constants/index';

import StyledAutocomplete, { StyledPopper } from './Autocomplete.styles';
import { AutocompleteProps, AutocompleteOptions } from './Autocomplete.types';

const Autocomplete: FunctionComponent<AutocompleteProps> = (
  props: AutocompleteProps
): JSX.Element => {
  const {
    open,
    value,
    loading,
    multiple,
    variant,
    select,
    error,
    disabled,
    noOptionsText,
    textFieldProps,
    renderTags,
    renderOption,
    onChange,
    ...resProps
  } = props;

  /** States */
  const valueFlag =
    variant !== 'primary' &&
    (multiple && value ? (value as AutocompleteOptions[]).length : value);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [hideLabel, setHideLabel] = useState<boolean>(!!valueFlag);
  const [selectReason, setSelectReason] = useState<string>(
    hideLabel ? 'hide-placeholder' : 'show-placeholder'
  );

  useEffect(() => {
    setHideLabel(!!valueFlag);
  }, [valueFlag]);

  /** Renders the Tags (Selected Values) when multiple prop is provided */
  const renderTagsHandler = (
    renderValue: any[],
    getTagProps: AutocompleteRenderGetTagProps
  ) =>
    renderValue.map((option: AutocompleteOptions, index: number): ReactNode => {
      const { onDelete, ...resTagProps } = getTagProps({ index });
      return (
        <Typography
          variant="input"
          component="span"
          sx={{
            color: getTagProps({ index }).disabled ? 'action.disabled' : '',
          }}
          {...resTagProps}
        >
          {`${option.label},`}
        </Typography>
      );
    });

  /** Renders the Dropdown with Checkboxes */
  const renderOptionHandler = (
    renderProps: React.HTMLAttributes<HTMLLIElement>,
    option: unknown,
    { selected }: AutocompleteRenderOptionState
  ): ReactNode => (
    <MenuItem
      {...renderProps}
      key={`${(option as AutocompleteOptions).value}${Math.random()}`}
    >
      {multiple && <Checkbox sx={{ mr: 2 }} checked={selected} />}{' '}
      <Typography variant="main" component="span">
        {(option as AutocompleteOptions).label}
      </Typography>
    </MenuItem>
  );

  /** Opens dropdown when clicked on textfield  */
  const selectOnClickHandler = () =>
    !disabled &&
    !textFieldProps?.InputProps?.readOnly &&
    select &&
    setIsOpen(!isOpen);

  /** Renders the text input field */
  const renderInput = (params: AutocompleteRenderInputParams): ReactNode => (
    <TextField
      onKeyUp={(e) => {
        if (e.key === CommonConstants.ENTER_KEY) {
          setIsOpen(true);
        } else if (e.key === 'Escape') {
          setIsOpen(false);
        }
      }}
      tabIndex={!textFieldProps?.InputProps?.readOnly ? 0 : undefined}
      {...params}
      classes={{ root: isOpen ? 'selectFieldset' : '' }}
      {...textFieldProps}
      InputLabelProps={{
        ...textFieldProps?.InputLabelProps,
        shrink: variant === 'primary',
        className: variant === 'secondary' ? 'selectLabel' : '',
      }}
      sx={{
        ...textFieldProps?.sx,
        '& .MuiOutlinedInput-root': {
          cursor: select ? 'pointer' : 'text',
        },
        '& .MuiOutlinedInput-root.Mui-disabled': {
          cursor: 'not-allowed',
        },
      }}
      InputProps={{
        ...params.InputProps,
        ...textFieldProps?.InputProps,
        role: select ? 'button' : undefined,
        onClick: selectOnClickHandler,
        inputProps: {
          ...params.inputProps,
          style:
            Boolean(valueFlag) && select && multiple ? { display: 'none' } : {},
          disabled: select || disabled,
          className:
            select && selectReason !== 'show-placeholder'
              ? `selectInput ${params.inputProps.className as string}`
              : params.inputProps.className,
          'aria-label': textFieldProps?.placeholder,
        },
        endAdornment: (
          <>
            {loading ? (
              <Loader size={22} sx={{ alignSelf: 'center', mr: 1 }} />
            ) : null}
            {params.InputProps.endAdornment}
          </>
        ),
      }}
      placeholder={textFieldProps?.placeholder}
      label={select && (!hideLabel ? textFieldProps?.label : '')}
      size={select && variant === 'primary' ? 'medium' : 'small'}
      error={error}
      helperText={textFieldProps?.helperText}
    />
  );

  /** Used to determine the string value for a given option */
  const getOptionLabel = (option: unknown): string =>
    (option as AutocompleteOptions).label;

  /** Used to get selected value */
  const isOptionEqualToValue = (
    option: unknown,
    selectedValue: unknown
  ): boolean =>
    (option as AutocompleteOptions).value ===
    (selectedValue as AutocompleteOptions).value;

  /** On dropdown value select */
  const onChangeHandler = (
    event: React.SyntheticEvent<Element, Event>,
    changeValue: unknown,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<unknown>
  ): void => {
    setSelectReason(reason);
    if (!isOpen && reason === 'clear') {
      event.stopPropagation();
    }
    if (
      multiple ? !(changeValue as AutocompleteOptions[]).length : changeValue
    ) {
      setHideLabel(false);
      setSelectReason('show-placeholder');
    } else if (variant === 'secondary') {
      setHideLabel(true);
    }
    if (onChange) {
      onChange(event, changeValue, reason, details);
    }
  };

  /** Dropdown and Clear Icons */
  const popupIcon = !textFieldProps?.InputProps?.readOnly && (
    <SvgIcon>
      {select && variant === 'primary' ? Icons.arrowDropDown : Icons.expandMore}
    </SvgIcon>
  );
  const clearIcon = !textFieldProps?.InputProps?.readOnly && (
    <SvgIcon>{Icons.close}</SvgIcon>
  );

  /** No Options Text */
  const noOptionsTextElement = (
    <Typography variant="input" component="p" sx={{ color: 'text.secondary' }}>
      {noOptionsText}
    </Typography>
  );

  /** Closes dropdown if clicked outside the element */
  const onClickAwayHandler = () =>
    selectReason !== 'selectOption' && selectReason !== 'removeOption'
      ? setIsOpen(false)
      : setSelectReason('');

  return (
    <ClickAwayListener onClickAway={onClickAwayHandler}>
      <StyledAutocomplete
        open={select ? isOpen : open}
        value={value}
        loading={loading}
        multiple={multiple}
        disabled={disabled}
        onChange={onChangeHandler}
        popupIcon={popupIcon}
        clearIcon={clearIcon}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        renderTags={renderTags || renderTagsHandler}
        renderOption={renderOption || renderOptionHandler}
        noOptionsText={noOptionsTextElement}
        renderInput={renderInput}
        PopperComponent={StyledPopper}
        {...resProps}
      />
    </ClickAwayListener>
  );
};

Autocomplete.defaultProps = {
  multiple: true,
  openOnFocus: true,
  disableCloseOnSelect: true,
  variant: 'primary',
  noOptionsText: 'No session available',
  textFieldProps: {
    label: '',
    placeholder: '',
  },
};

export default Autocomplete;
